import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import { genreMap, movies, regions, sortOptions, years } from "@/mocks/movies";
import type { MovieType } from "@/mocks/movies";
import FilterBar from "./components/FilterBar";

const PAGE_SIZE = 20;
const categoryTypes: MovieType[] = ["电影", "电视剧", "综艺"];

const introMap: Record<MovieType, { title: string; desc: string }> = {
  电影: {
    title: "电影频道",
    desc: "从院线新片到口碑佳作，按类型、地区与年份筛选，找到切合心情的那一部。",
  },
  电视剧: {
    title: "电视剧频道",
    desc: "国产剧、日韩剧与欧美剧集同步整理，支持按类型与年份快速筛选。",
  },
  综艺: {
    title: "综艺频道",
    desc: "真人秀、音乐竞演、旅行纪实与推理综艺，适合下饭和周末放松。",
  },
};

export default function Category() {
  const params = useParams<{ type: string }>();
  const rawType = params.type ? decodeURIComponent(params.type) : "";
  const type = categoryTypes.includes(rawType as MovieType) ? (rawType as MovieType) : null;

  const [filters, setFilters] = useState({
    region: "全部",
    year: "全部",
    genre: "全部",
    sort: "最新上线",
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters({ region: "全部", year: "全部", genre: "全部", sort: "最新上线" });
    setPage(1);
  }, [type]);

  const meta = type ? introMap[type] : null;

  usePageMeta({
    title: type ? `${type} - 看片免费app网站` : "影视分类 - 看片免费app网站",
    description: type
      ? `${meta?.desc} 看片免费app网站整理${type}资源介绍、评分与适配看片应用推荐。`
      : "浏览看片免费app网站的影视分类，包含电影、电视剧与综艺。",
    keywords: type
      ? `${type},${type}推荐,免费${type},高清${type},看片免费app网站`
      : "影视分类,电影,电视剧,综艺,看片免费app网站",
  });

  const filtered = useMemo(() => {
    if (!type) return [];
    const list = movies.filter((movie) => {
      if (movie.type !== type) return false;
      if (filters.region !== "全部" && movie.region !== filters.region) {
        if (!(filters.region === "其他" && !["中国大陆", "中国香港", "美国", "日本", "韩国", "英国", "法国"].includes(movie.region))) {
          return false;
        }
      }
      if (filters.year !== "全部" && String(movie.year) !== filters.year) return false;
      if (filters.genre !== "全部" && !movie.genres.includes(filters.genre)) return false;
      return true;
    });

    return list.sort((a, b) => {
      if (filters.sort === "评分最高") return b.rating - a.rating;
      if (filters.sort === "人气最热") return parseFloat(b.heat) - parseFloat(a.heat);
      return b.year - a.year || b.rating - a.rating;
    });
  }, [type, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilterChange = (key: "region" | "year" | "genre" | "sort", value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 320, behavior: "smooth" });
  };

  if (!type) {
    return (
      <SiteLayout>
        <div className="w-full max-w-page mx-auto px-4 md:px-8">
          <EmptyState
            icon="ri-error-warning-line"
            title="该分类暂不存在"
            description="你访问的分类可能已调整，可以先回到首页，或从电影、电视剧、综艺中重新选择。"
            primaryLabel="返回首页"
            secondaryLabel="浏览电影频道"
            secondaryTo="/category/电影"
          />
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-page mx-auto px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb
            dark
            items={[
              { label: "首页", to: "/" },
              { label: "影视分类", to: "/category/电影" },
              { label: type },
            ]}
          />
          <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-background-50">{meta?.title}</h1>
              <p className="mt-2 text-sm text-primary-200 max-w-2xl leading-6">{meta?.desc}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {categoryTypes.map((item) => (
                <Link
                  key={item}
                  to={`/category/${item}`}
                  className={`px-4 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                    item === type
                      ? "bg-accent-500 text-primary-950"
                      : "border border-primary-700 text-primary-200 hover:border-accent-400 hover:text-accent-300"
                  }`}
                >
                  {item}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-6">
        <FilterBar
          genreOptions={genreMap[type]}
          filters={filters}
          onChange={handleFilterChange}
          regions={regions}
          years={years}
          sortOptions={sortOptions}
          total={filtered.length}
        />
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-6">
        {pageItems.length === 0 ? (
          <EmptyState
            icon="ri-filter-off-line"
            title="没有符合筛选条件的作品"
            description="试试放宽年份或地区条件，也可以直接查看该分类的全部内容。"
            primaryLabel="清空筛选条件"
            primaryTo={`/category/${type}`}
            secondaryLabel="返回首页"
            secondaryTo="/"
          />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <div className="mt-8 flex flex-col items-center gap-3">
              <Pagination page={currentPage} totalPages={totalPages} onChange={handlePageChange} />
              <p className="text-xs text-foreground-400">
                第 {currentPage} / {totalPages} 页 · 每页 {PAGE_SIZE} 条
              </p>
            </div>
          </>
        )}
      </div>
    </SiteLayout>
  );
}